import { useEffect } from 'react';
import { React, useState } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as api from 'services/api';
import { useTranslation } from 'react-i18next';
import { useUserContext } from 'contexts/UserContext';
import { fetchConfig } from '_helpers/fetch-helpers';

export function UserForm({ userFormData, handleSubmit, handleInputChange, error, setError, isEditing }) {
  const { t } = useTranslation();
  const [controlCentres, setControlCentres] = useState([]);
  const [roleHierarchy, setRoleHierarchy] = useState([]);
  const navigate = useNavigate();
  const { userData } = useUserContext();

  useEffect(() => {
    async function fetchControlCentres() {
      const response = await api.getControlCentres();
      if (response.status === 200) {
        setControlCentres(response.data);
      } else {
        console.error('Error al obtener centros de control');
      }
    }

      fetchControlCentres();

      fetchConfig()
          .then(config => setRoleHierarchy(config?.roleHierarchy || []))
          .catch(() => setRoleHierarchy(['Admin', 'UserManager', 'User']));

  }, []);
  
  const isStrongPassword = (password) => {
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+}{"':;?/>.<,])(?=.{9,})/;
    return passwordRegex.test(password);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    const fieldNames = {
      name: t('usName'),
      surnames: t('usSurnames'),
      phone: t('usPhone'),
      username: t('user'),
        password: t('password'),
      role: t('role'),
      controlCentreId: t('usControlCentre')
    };

    for (const field of Object.keys(fieldNames)) {
      if (!isEditing) {
          if (!userFormData[field]) {
            setError(`${fieldNames[field]} ${t('usFieldRequired')}`);
          return;
        }
      }
    }

    if (!isEditing) {
        if (!isStrongPassword(userFormData.password)) {
          setError(t('usPassRequirements'));
        return;
      }
    }

    setError('');

    handleSubmit();
  };

  const handleCancel = () => {
    setError('');
    navigate('/userManagement');
  };

    // Devuelve el rol del usuario y los inferiores
    const getCreatableRoles = (currentUserRole) => {
        const idx = roleHierarchy.indexOf(currentUserRole);
        if (idx === -1) return [];
        return roleHierarchy.slice(idx);
    };


  return (
    <Form className="mt-5" onSubmit={handleFormSubmit}>
      <Stack direction="horizontal" gap={4} className="justify-content-between">
        <Form.Group controlId="name" className="col-3">
          <Form.Label className="small">{t('usName')}</Form.Label>
          <Form.Control
            type="text"
            className="bg-light"
            name="name"
            value={userFormData.name}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group controlId="surnames" className="col-5">
          <Form.Label className="small">{t('usSurnames')}</Form.Label>
          <Form.Control
            type="text"
            className="bg-light"
            name="surnames"
            value={userFormData.surnames}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group controlId="phone" className="col-3">
            <Form.Label className="small">{t('usPhone')}</Form.Label>
            <Form.Control
                type="text"
                className="bg-light"
                name="phone"
                value={userFormData.phone}
                onChange={handleInputChange}
            />
        </Form.Group>
      </Stack>
      <Stack direction="horizontal" gap={4} className="mt-4 justify-content-between">
        <Form.Group controlId="username" className="col-4">
          <Form.Label className="small">{t('user')}</Form.Label>
          <Form.Control
            type="text"
            className="bg-light"
            name="username"
            value={userFormData.username}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group controlId="password" className="col-4">
          <Form.Label className="small">{t('password')}</Form.Label>
          <Form.Control
            type="text"
            className="bg-light"
            name="password"
            value={userFormData.password}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group controlId="rol">
          <Form.Label className="small">{t('usRole')}</Form.Label>
        <Form.Select
            name="role"
            className="bg-light"
                      value={userFormData.role}
            onChange={(event) => {
                const userRole = event.target.value;
                handleInputChange({
                    target: {
                        name: 'role',
                        value: userRole,
                    },
                });
            }}>
                      <option key={0} value={0}>{t('usSelectRole')}</option>
            {getCreatableRoles(userData.role).map((role) => (

                <option key={role} value={role}>
                    {t('us'+role)}
                </option>
            ))}
        </Form.Select>
        </Form.Group>
      </Stack>

      <Stack direction="horizontal" gap={4} className="mt-4">
      <Form.Group controlId="controlCentreId">
        <Form.Label className="small">{t('usControlCentre')}</Form.Label>
        <Form.Select
          name="controlCentreId"
          className="bg-light"
          value={userFormData.controlCentreId}
          onChange={(event) => {
            handleInputChange({
              target: {
                name: 'controlCentreId',
                value: parseInt(event.target.value)
              }
            });
          }}
        >
          <option value="0">{t('usControlCentre')}</option>
          {controlCentres.map((centre) => (
            <option key={centre.controlCentreId} value={centre.controlCentreId}>
              {centre.controlCentreText}
            </option>
          ))}
        </Form.Select>
        </Form.Group>

        <Form.Group controlId="estado">
            <Form.Label className="small">Estado</Form.Label>
            <Form.Select
                name="admin"
                className="bg-light"
                value={userFormData.isActive}
                onChange={(event) => {
                    const isActive = event.target.value === 'true';
                    handleInputChange({
                        target: {
                            name: 'isActive',
                            value: isActive,
                        },
                    });
                }}
            >
                <option value={'true'}>Activo</option>
                <option value={'false'}>Bloqueado</option>
            </Form.Select>
        </Form.Group>
      </Stack>

      <Stack direction="horizontal" className="justify-content-start">
        {error && <p className="text-danger fw-semibold">{error}</p>}
      </Stack>

      <Stack direction="horizontal" gap={4} className="mt-3">
        <Button type="submit" variant="dark" className="mt-3">{t('usSave')}</Button>
        <Button variant="outline-dark" className="mt-3" onClick={handleCancel}>{t('usCancel')}</Button>
      </Stack>
    </Form>
  );
}
